/**
 * The Meta ads panel, and its rows. Slice 44 moved the drafts onto the
 * campaigns page — a Meta ad is a campaign on another surface — into a panel
 * of their own under the email and WhatsApp campaigns, with the surfaces each
 * runs on. Slice 65 split the old Reach pillar into Reach (paid ads) and
 * Connect (email and WhatsApp), and the panel moved with the ads to Reach's
 * own page, `/reach/ads` (`routes/reach/ReachAdsPage.tsx`), unchanged: what
 * it draws, and why, is below.
 *
 * ## A PANEL, NOT MORE ROWS IN THE CAMPAIGNS TABLE
 *
 * The table's columns are a message's: who it reaches on its channel, how
 * many were sent and skipped, when it was scheduled. An ad has none of them —
 * no recipients, no send, a budget and an end instead — so rows of ads in that
 * table would be rows of empty cells beside full ones, the one-column-two-
 * meanings trap every report in this product keeps channels apart for. Each
 * list keeps columns that mean one thing.
 *
 * ## ONE PILL PER ROW: THE STATUS
 *
 * Each row is a Meta campaign composed here — the name, when it changed, the
 * budget as money, and its own status badge. The surfaces it runs on are the
 * row's small grey line, not more pills: four identical pills beside each
 * other made the status the one nobody could find, and a bare "Automatic"
 * beside "Paused" read as a way the ad runs rather than where. A draft's
 * placements are any of Facebook, Instagram and WhatsApp Status (a chat
 * draft's, with Instagram beside it — slice 47); none chosen is automatic
 * placements — Meta decides, and that covers Facebook and Instagram ads and
 * whatever else Meta serves, WhatsApp Status among what it may — so it reads
 * "Facebook ads · Instagram ads · Automatic placements" rather than naming a
 * surface it cannot promise.
 *
 * ## THE PANEL'S STATES, AND WHY IT IS PURE
 *
 * The page fetches; this draws — so the suite renders every state without a
 * browser. A workspace with no drafts and nothing asked of the list gets no
 * panel at all: on the campaigns page that kept a placeholder for a surface
 * the workspace did not use off someone else's screen, and on the Reach page
 * the page draws its own first-run state there instead. A read that failed is a
 * "Not read" chip with a retry, never a second alert under the table's: one
 * outage, one alert. The count is of campaigns — "draft" is the unpublished
 * badge's word, and a panel that counted a started campaign as "1 draft" gave
 * one word three meanings on one page.
 *
 * ## THE LIST IS CONTINUED OR COMPLETE — SLICE 52, GAP 4
 *
 * Slice 42 listed the newest hundred. Slice 44 admitted it with a "Partial
 * list" chip and filtered those hundred in the browser, so "No match" could
 * mean "not among the newest hundred": a chip that confessed the defect
 * instead of fixing it, above a search that was lying about its own scope.
 *
 * Both are gone. The search and the surface filter run in SQL over the whole
 * of the tenant's drafts, and the list is read 25 rows at a time: either it
 * ends with "Show more", or it is complete and ends with nothing. There is no
 * state left in which rows exist and the panel does not say so, which is what
 * the "Partial list" chip was for — and a chip that can never fire is the next
 * reviewer's finding, so it went with the truncation it confessed.
 *
 * The header counts the rows loaded, each list counting itself as slice 44
 * settled. Not a total: a count query per keystroke prints a number nobody
 * acts on, and the contract answers none. "N campaigns" above a "Show more"
 * is therefore how many are on screen — which is what the footer of a
 * continued list means everywhere else in this product.
 *
 * A LATER PAGE THAT FAILED IS NOT THE PANEL'S "NOT READ" STATE. The first
 * read failing leaves nothing to draw, so the whole panel is a "Not read" chip
 * with a retry. A page beyond the first failing is different: the rows already
 * read are still true, so they stay, and the chip and the retry go in the
 * footer beside the button that asked for the page. Both retries restart from
 * the first page — see the footer for why retrying the cursor that failed is
 * not offered, and why a footer with neither would be a dead end.
 *
 * "No match" keeps its shape and is true for the first time: the search has
 * looked at the whole set, so it is a statement about the workspace rather
 * than about its newest hundred. It is also the panel's only empty chip now —
 * see the comment on it for why "None yet" could not survive the move of the
 * filtering to the server without becoming a chip that might be false.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner, StateChip } from '@wend/ui';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/* `@wend/shared` directly, never through the API client. */
import { surfacesOfPlacements, type MetaAdDraftRecord } from '@wend/shared';

import { formatDate, pluralise } from '../../lib/format';
import { RetryWait, useRetryWait } from '../../lib/retry-wait';
import { surfaceLabel } from '../../lib/surface-display';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { DraftStatusBadge } from './MetaAdDraftSummary';

/* -------------------------------------------------------------------------- */
/* The panel                                                                   */
/* -------------------------------------------------------------------------- */

export interface MetaAdsPanelProps {
  /** The rows read so far, across every page loaded. */
  readonly drafts: readonly MetaAdDraftRecord[];
  /** No page has arrived yet — the first read is in flight. */
  readonly loading: boolean;
  /** A page is in flight over rows already drawn. */
  readonly loadingMore: boolean;
  /** The read failed and there is no answer to draw. */
  readonly failed: boolean;
  /**
   * A page beyond the first failed, over rows already drawn. The rows stay and
   * the retry sits beside "Show more" — see the footer for why pressing "Show
   * more" again cannot be the way out of it.
   */
  readonly failedMore: boolean;
  /** What the failed read said, so a refusal can carry its own wait. */
  readonly error: unknown;
  readonly onRetry: () => void;
  /** Whether a further page can be asked for; `null` withdraws "Show more". */
  readonly onMore: (() => void) | null;
  /** Whether a search or a surface filter is on — which is what makes an empty list "No match". */
  readonly filtered: boolean;
  readonly now: number;
}

export function MetaAdsPanel({
  drafts,
  loading,
  loadingMore,
  failed,
  failedMore,
  error,
  onRetry,
  onMore,
  filtered,
  now,
}: MetaAdsPanelProps) {
  if (failed) {
    return (
      <Card data-role="meta-ads-panel" data-state="failed">
        <PanelHeader />
        <CardContent className="flex flex-wrap items-center gap-2 p-[18px] pt-0">
          <StateChip state="not_measured" label="Not read" />
          <RetryButton error={error} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    /* Only while a filter is on: an unfiltered first load draws no panel at
       all until it answers, because a workspace that uses no ads should not
       flash a Meta ads card at every visit to this page. */
    if (!filtered) return null;
    return (
      <Card data-role="meta-ads-panel" data-state="loading">
        <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
          <Spinner size="md" label={null} />
          <span role="status">Loading…</span>
        </CardContent>
      </Card>
    );
  }

  /* A workspace with no ads at all, and nothing asked of the list, gets no
     panel — the same rule as before paging, now decided from the whole set
     rather than from the newest hundred. */
  if (drafts.length === 0 && !filtered) return null;

  return (
    <Card data-role="meta-ads-panel" data-state="ok">
      <PanelHeader count={drafts.length} />
      {drafts.length === 0 ? (
        <CardContent className="p-[18px] pt-0">
          {/*
            "No match" is now true of every draft this workspace has, because
            the search ran in SQL. Before slice 52 it meant "not among the
            hundred rows this browser is holding".

            It is the panel's ONLY empty chip since this slice. "None yet" used
            to sit beside it, told apart by comparing the rows after the
            browser's own filter with the rows before it — a distinction that
            existed only because the browser held the unfiltered hundred. The
            filtering is the server's now and the contract answers no total, so
            an empty page under a surface filter cannot be told from a
            workspace with no drafts at all without a second query per
            keystroke, which gap 4 declined. Rather than print a chip that
            might be false, the panel prints the one that cannot be: nothing
            matched what was asked. An unfiltered empty list draws no panel,
            which is slice 44's rule and unchanged.
          */}
          <Badge tone="neutral" data-role="meta-ad-drafts-empty">
            No match
          </Badge>
        </CardContent>
      ) : (
        <>
          <CardContent className="p-0">
            <MetaAdDraftList drafts={drafts} now={now} />
          </CardContent>
          {(onMore !== null || failedMore) && (
            <CardContent className="flex flex-wrap items-center gap-2 p-[18px] pt-3">
              {onMore !== null && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  data-role="meta-ad-drafts-more"
                  loading={loadingMore}
                  onClick={onMore}
                >
                  Show more
                </Button>
              )}
              {/*
                A LATER PAGE THAT FAILED IS SAID HERE, BESIDE THE BUTTON THAT
                ASKED FOR IT.

                Before this, a 400 for a cursor the API could not read — or the
                network — on page two drew nothing at all: the rows already on
                screen kept the panel out of its "Not read" state, and pressing
                "Show more" again did nothing for ever, because the cursor was
                already the one that failed and moving it to itself re-reads
                nothing. The chip says the page was not read, and the retry is
                wired to the restart rather than to another attempt at that
                cursor — a cursor the API refused once it would refuse again.
              */}
              {failedMore && (
                <span data-role="meta-ad-drafts-more-failed" className="flex flex-wrap items-center gap-2">
                  <StateChip state="not_measured" label="Not read" />
                  <RetryButton error={error} onRetry={onRetry} />
                </span>
              )}
            </CardContent>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The retry on a failed read, with the wait beside it when a limit asked for
 * one — Wend's own read limit, or Meta's throttle. The button keeps its name
 * and is disabled until the wait runs out; see `lib/retry-wait`.
 */
function RetryButton({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const wait = useRetryWait(error);
  return (
    <>
      <Button type="button" variant="ghost" size="sm" disabled={wait !== null} onClick={onRetry}>
        Try again
      </Button>
      <RetryWait seconds={wait} />
    </>
  );
}

/*
 * "Composed in Wend", not "Meta ads". The panel was titled "Meta ads"
 * while it was a guest under the campaigns table, where the name said what the
 * unfamiliar card was. Since slice 65 it is the body of Reach's own page, whose
 * top bar already says "Meta ads" — and a second heading of the same words,
 * directly under the first, is heard twice by a screen reader and read as a
 * repeat by everyone else. The title now says what is in the list — the ad
 * campaigns composed here, published or not — without the word "draft", which
 * is the unpublished badge's word and never the panel's.
 */
function PanelHeader({ count = 0 }: { count?: number }) {
  return (
    <CardHeader className="flex-row flex-wrap items-center gap-3 space-y-0 pb-3">
      <CardTitle>Composed in Wend</CardTitle>
      {count > 0 && (
        <span data-role="meta-ads-panel-count" className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
          {pluralise(count, 'campaign')}
        </span>
      )}
    </CardHeader>
  );
}

/* -------------------------------------------------------------------------- */
/* The rows                                                                    */
/* -------------------------------------------------------------------------- */

export interface MetaAdDraftListProps {
  readonly drafts: readonly MetaAdDraftRecord[];
  readonly now: number;
}

export function MetaAdDraftList({ drafts, now }: MetaAdDraftListProps) {
  return (
    <ul data-role="meta-ad-drafts" className="divide-y divide-line">
      {drafts.map((record) => (
        <li key={record.id}>
          <Link
            to={`/reach/ads/${record.id}`}
            className="group flex flex-wrap items-center justify-between gap-3 px-[18px] py-3.5 hover:bg-white/[0.03]"
          >
            <span className="flex min-w-0 flex-col gap-1">
              <span className="truncate text-[0.92rem] text-text group-hover:underline group-hover:underline-offset-2">
                {record.draft.campaign.name}
              </span>
              <span className="font-mono text-[0.72rem] text-muted">
                <MetaAdDraftSurfaces placements={record.draft.adSet.targeting.placements} />
                {' · '}Updated {formatDate(record.updatedAt)}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-[0.88rem] tabular-nums text-text">
                {formatMetaMoney(record.draft.adSet.budget.amount, record.currency) ?? record.currency}
                <span className="text-muted">{record.draft.adSet.budget.kind === 'daily' ? ' / day' : ' total'}</span>
              </span>
              <DraftStatusBadge record={record} now={now} />
              <ChevronRight aria-hidden="true" className="size-4 text-muted" />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** The surfaces a draft runs on, as words — the ones it names, and automatic placements, when Meta decides. */
export function MetaAdDraftSurfaces({ placements }: { placements: readonly ('facebook' | 'instagram' | 'whatsapp')[] | null }) {
  const words = [
    ...surfacesOfPlacements(placements).map((surface) => surfaceLabel(surface)),
    ...(placements === null ? ['Automatic placements'] : []),
  ];
  return <span data-role="meta-ad-draft-surfaces">{words.join(' · ')}</span>;
}
